import { useEffect, useRef, useState } from "react";
import { Fingerprint } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const supported = () => Boolean(window.isSecureContext && window.PublicKeyCredential && navigator.credentials);
const decode = (value) => Uint8Array.from(atob(value.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(value.length / 4) * 4, "=")), c => c.charCodeAt(0));
const encode = (value) => btoa(String.fromCharCode(...new Uint8Array(value))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");

export async function passkeyCeremony(kind, password) {
  const { data } = await api.post(`/auth/passkeys/${kind}/options`, kind === "register" ? { password } : {});
  const options = { ...data.publicKey, challenge: decode(data.publicKey.challenge) };
  if (options.user) options.user = { ...options.user, id: decode(options.user.id) };
  for (const field of ["excludeCredentials", "allowCredentials"]) {
    if (options[field]) options[field] = options[field].map(c => ({ ...c, id: decode(c.id) }));
  }
  const credential = await navigator.credentials[kind === "register" ? "create" : "get"]({ publicKey: options });
  if (!credential) throw new Error("Passkey request was cancelled.");
  const response = {};
  for (const field of ["clientDataJSON", "attestationObject", "authenticatorData", "signature", "userHandle"]) {
    if (credential.response[field]) response[field] = encode(credential.response[field]);
  }
  if (credential.response.getTransports) response.transports = credential.response.getTransports();
  return (await api.post(`/auth/passkeys/${kind}/verify`, {
    ceremony_id: data.ceremony_id,
    credential: { id: credential.id, rawId: encode(credential.rawId), type: credential.type,
      response, clientExtensionResults: credential.getClientExtensionResults() },
  })).data;
}

const errorText = (error) => ["NotAllowedError", "AbortError"].includes(error?.name)
  ? "Passkey request cancelled or timed out. You can still use your password."
  : formatApiError(error);

let googleLoader;
function loadGoogle() {
  if (window.google?.accounts?.id) return Promise.resolve();
  if (!googleLoader) googleLoader = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.onload = resolve;
    script.onerror = () => { googleLoader = null; reject(new Error("Google sign-in could not load.")); };
    document.head.appendChild(script);
  });
  return googleLoader;
}

export function GoogleSignin({ signup = false, password = "", onSuccess, onProfile, onError }) {
  const target = useRef(null);
  const callbacks = useRef({ password, onSuccess, onProfile, onError });
  callbacks.current = { password, onSuccess, onProfile, onError };
  useEffect(() => {
    let active = true;
    api.get("/auth/signin-options").then(async ({ data }) => {
      if (!data.google_client_id || !active) return;
      await loadGoogle();
      if (!active || !target.current) return;
      window.google.accounts.id.initialize({ client_id: data.google_client_id, auto_select: false,
        callback: async ({ credential }) => {
          if (!active) return;
          try {
            if (signup) {
              const { data: profile } = await api.post("/auth/google/profile", { credential });
              callbacks.current.onProfile?.(profile, credential);
            } else {
              const result = await api.post("/auth/google", { credential, password: callbacks.current.password });
              callbacks.current.onSuccess?.(result.data);
            }
          } catch (error) { callbacks.current.onError?.(formatApiError(error)); }
        },
      });
      window.google.accounts.id.renderButton(target.current, { type: "standard", theme: "outline",
        size: "large", text: signup ? "signup_with" : "signin_with", width: Math.min(360, target.current.clientWidth || 300) });
    }).catch(() => { /* Email/password remain available if Google is unreachable. */ });
    return () => { active = false; };
  }, [signup]);
  return <div ref={target} className="mb-4 flex w-full justify-center" data-testid="google-signin" />;
}

export function PasskeySignin({ onSuccess, onError }) {
  const [busy, setBusy] = useState(false);
  if (!supported()) return null;
  const signIn = async () => {
    setBusy(true);
    try { onSuccess(await passkeyCeremony("login")); }
    catch (error) { onError(errorText(error)); }
    finally { setBusy(false); }
  };
  return <div className="mt-6 border-t border-slate-200 pt-5">
    <button type="button" onClick={signIn} disabled={busy} data-testid="passkey-signin"
      className="flex w-full items-center justify-center gap-2 rounded-full border border-[#2F6FA5]/40 bg-white px-4 py-3 font-bold text-[#2F6FA5] disabled:opacity-50">
      <Fingerprint className="h-5 w-5" />{busy ? "Checking passkey..." : "Sign in with a passkey"}
    </button>
    <p className="mt-2 text-center text-xs text-slate-500">Use a passkey you previously saved for FEA Slots.</p>
  </div>;
}

export function PasskeySettings({ dark = false }) {
  const [keys, setKeys] = useState([]);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const refresh = () => api.get("/auth/passkeys").then(({ data }) => setKeys(data.passkeys));
  useEffect(() => { refresh().catch(() => {}); }, []);
  if (!supported()) return null;
  const action = async (id) => {
    setBusy(true); setMessage("");
    try {
      if (id) await api.post(`/auth/passkeys/${encodeURIComponent(id)}/remove`, { password });
      else await passkeyCeremony("register", password);
      setPassword(""); await refresh(); setMessage(id ? "Passkey removed." : "Passkey saved. Use it the next time you sign in.");
    } catch (error) { setMessage(errorText(error)); }
    finally { setBusy(false); }
  };
  return <details className={`mb-6 rounded-2xl border p-4 ${dark ? "border-white/10 bg-[#081421] text-white" : "border-slate-200 bg-white text-slate-950"}`}>
    <summary className="cursor-pointer font-bold">Passkeys & sign-in security</summary>
    <p className="my-3 text-sm">Save a passkey to sign in with your fingerprint, face or device PIN. Your password stays available.</p>
    <label className="block text-sm">Confirm your account password
      <input type="password" autoComplete="current-password" value={password} onChange={e => setPassword(e.target.value)}
        className="mt-2 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-950" />
    </label>
    <button type="button" disabled={busy || !password} onClick={() => action()} className="my-3 rounded-full bg-[#2F6FA5] px-5 py-2 font-bold text-white disabled:opacity-50">{busy ? "Please wait..." : "Add a passkey"}</button>
    {keys.map((key, i) => <div key={key.id} className="flex items-center justify-between gap-3 py-2 text-sm">
      <span>Passkey {i + 1} · {new Date(key.created_at).toLocaleDateString()}</span>
      <button type="button" disabled={busy || !password} onClick={() => action(key.id)} className="underline disabled:opacity-50">Remove</button>
    </div>)}
    {message && <p role="status" className="mt-2 text-sm">{message}</p>}
  </details>;
}
